<template>
  <div class="media-preview">
    <button class="btn btn-header-start btn-danger no-pseudo" v-if="canDelete" @click="$emit('delete')">
      <i class="fa-solid fa-trash-alt" />
    </button>
    <button class="btn btn-header-start btn-primary no-pseudo" style="left: calc(35px + 1.5rem);" v-if="canEdit" @click="$emit('edit')">
      <i class="fa-solid fa-pencil" />
    </button>
    <button class="btn btn-header-start btn-success no-pseudo" :style="downloadButtonStyle" v-if="pdfBlob" @click="downloadPdf">
      <i class="fa-solid fa-download" />
    </button>
    <button
      type="button"
      class="btn btn-header-end btn-light btn-hover-danger no-pseudo"
      @click="closePreview"
      aria-label="Close"
    >
      <i class="fa-solid fa-times" />
    </button>

    <div class="media-container">
      <div v-if="numPages" ref="scrollArea" class="pdf-scroll" @scroll="onScroll"
        style="height: calc(100% - 47px - 1rem); margin-top: calc(47px + 1rem); overflow: auto; background: #525659; padding: 1rem 0;">
        <canvas v-for="page in numPages" :key="page" :ref="`canvas${page}`"
          class="mb-3 d-block mx-auto"
          style="max-width: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></canvas>
      </div>
      <div v-if="numPages" class="pdf-toolbar">
        <button class="pdf-toolbar-btn" @click="prevPage" :disabled="currentPage <= 1">
          <i class="fa-solid fa-chevron-up"></i>
        </button>
        <span class="pdf-toolbar-text">{{ currentPage }} / {{ numPages }}</span>
        <button class="pdf-toolbar-btn" @click="nextPage" :disabled="currentPage >= numPages">
          <i class="fa-solid fa-chevron-down"></i>
        </button>
        <span class="pdf-toolbar-divider"></span>
        <button class="pdf-toolbar-btn" @click="zoomOut" :disabled="scale <= 0.5">
          <i class="fa-solid fa-magnifying-glass-minus"></i>
        </button>
        <span class="pdf-toolbar-text">{{ Math.round(scale * 100) }}%</span>
        <button class="pdf-toolbar-btn" @click="zoomIn" :disabled="scale >= 3">
          <i class="fa-solid fa-magnifying-glass-plus"></i>
        </button>
      </div>
      <div v-else-if="errorMessage" class="text-white text-center"
        style="margin-top: calc(47px + 1rem)">
        <p class="mb-2"><i class="fa-solid fa-triangle-exclamation fa-2x"/></p>
        <p>{{ errorMessage }}</p>
        <a :href="url" target="_blank" rel="noopener" class="btn btn-light btn-sm mt-2">
          Open in new tab
        </a>
      </div>
      <div v-else class="text-white text-center" style="margin-top: calc(47px + 1rem)">
        <div class="spinner-border" role="status"></div>
        <p class="mt-2 mb-0">Loading PDF…</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL || "/"}pdf.worker.min.js`;

export default {
  name: "pdf-preview",
  props: ["url", "canDelete", "canEdit"],
  data() {
    return {
      numPages: 0,
      errorMessage: null,
      loadingTask: null,
      pdfDoc: null,
      pdfBlob: null,
      scale: 1.0,
      currentPage: 1,
    };
  },
  computed: {
    filename() {
      try {
        const u = new URL(this.url, location.href);
        const last = u.pathname.split("/").pop() || "document.pdf";
        return decodeURIComponent(last) || "document.pdf";
      } catch (e) {
        return "document.pdf";
      }
    },
    downloadButtonStyle() {
      let n = 0;
      if (this.canDelete) n++;
      if (this.canEdit) n++;
      if (n === 0) return {};
      return { left: `calc((35px + 1.5rem) * ${n})` };
    },
  },
  watch: {
    url: {
      immediate: true,
      handler(newUrl) {
        this.cleanup();
        this.errorMessage = null;
        if (newUrl) this.loadPdf(newUrl);
      },
    },
  },
  methods: {
    async loadPdf(url) {
      try {
        const res = await this.API.getExternal(url, "file");
        this.pdfBlob = res.data;
        const buf = await this.pdfBlob.arrayBuffer();
        const data = new Uint8Array(buf);
        this.loadingTask = pdfjsLib.getDocument({ data });
        this.pdfDoc = await this.loadingTask.promise;
        this.numPages = this.pdfDoc.numPages;
        await this.$nextTick();
        for (let p = 1; p <= this.numPages; p++) {
          if (!this.pdfDoc) return;
          try {
            await this.renderPage(p);
          } catch (e) {
            console.error(`Failed to render page ${p}:`, e);
          }
        }
      } catch (err) {
        console.error("Failed to load PDF:", err, { url, status: err.response?.status });
        this.errorMessage = err.response?.status
          ? `Could not load PDF (HTTP ${err.response.status}).`
          : "Could not load PDF (CORS or network error).";
      }
    },
    async renderPage(pageNum) {
      if (!this.pdfDoc) return;
      const page = await this.pdfDoc.getPage(pageNum);
      const ref = this.$refs[`canvas${pageNum}`];
      const canvas = Array.isArray(ref) ? ref[0] : ref;
      if (!canvas) return;
      const containerWidth = (canvas.parentElement.clientWidth || 800) - 16;
      const baseViewport = page.getViewport({ scale: 1 });
      const fitScale = Math.min(containerWidth / baseViewport.width, 2) * 0.75;
      const cssScale = fitScale * this.scale;
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: cssScale * dpr });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
      }).promise;
    },
    downloadPdf() {
      if (!this.pdfBlob) return;
      const blobUrl = URL.createObjectURL(this.pdfBlob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = this.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    },
    prevPage() {
      if (this.currentPage > 1) this.goToPage(this.currentPage - 1);
    },
    nextPage() {
      if (this.currentPage < this.numPages) this.goToPage(this.currentPage + 1);
    },
    goToPage(pageNum) {
      const ref = this.$refs[`canvas${pageNum}`];
      const canvas = Array.isArray(ref) ? ref[0] : ref;
      if (canvas) canvas.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async zoomIn() {
      this.scale = Math.min(parseFloat((this.scale + 0.25).toFixed(2)), 3);
      await this.rerenderAll();
    },
    async zoomOut() {
      this.scale = Math.max(parseFloat((this.scale - 0.25).toFixed(2)), 0.5);
      await this.rerenderAll();
    },
    async rerenderAll() {
      for (let p = 1; p <= this.numPages; p++) {
        if (!this.pdfDoc) return;
        try {
          await this.renderPage(p);
        } catch (e) {
          console.error(`Failed to re-render page ${p}:`, e);
        }
      }
    },
    onScroll() {
      if (this._scrollTimer) clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        const container = this.$refs.scrollArea;
        if (!container) return;
        const mid = container.scrollTop + container.clientHeight / 2;
        for (let p = 1; p <= this.numPages; p++) {
          const ref = this.$refs[`canvas${p}`];
          const canvas = Array.isArray(ref) ? ref[0] : ref;
          if (!canvas) continue;
          const top = canvas.offsetTop;
          const bottom = top + canvas.offsetHeight;
          if (top <= mid && bottom >= mid) {
            this.currentPage = p;
            return;
          }
        }
      }, 100);
    },
    cleanup() {
      if (this.loadingTask) {
        try { this.loadingTask.destroy(); } catch (e) { /* noop */ }
        this.loadingTask = null;
      }
      this.pdfDoc = null;
      this.numPages = 0;
      this.pdfBlob = null;
      this.scale = 1.0;
      this.currentPage = 1;
    },
    closePreview() {
      this.$emit("close");
    },
  },
  mounted() {
    this.onKeydown = (e) => {
      if (e.key === "Escape") this.closePreview();
    };
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    if (this.onKeydown) {
      document.removeEventListener("keydown", this.onKeydown);
    }
    this.cleanup();
    this.closePreview();
  },
};
</script>

<style scoped>
.pdf-toolbar {
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: white;
  z-index: 10;
  user-select: none;
  backdrop-filter: blur(4px);
}
.pdf-toolbar-btn {
  background: transparent;
  border: none;
  color: white;
  padding: 0.25rem 0.6rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: opacity 0.15s;
}
.pdf-toolbar-btn:hover:not(:disabled) {
  opacity: 0.7;
}
.pdf-toolbar-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.pdf-toolbar-text {
  min-width: 50px;
  text-align: center;
  font-size: 0.875rem;
}
.pdf-toolbar-divider {
  border-left: 1px solid rgba(255, 255, 255, 0.25);
  height: 20px;
  margin: 0 0.5rem;
}
</style>
